import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isAfter,
  endOfDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@polichat/flamboyant';

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const Popover = styled.div`
  position: absolute;
  top: 48px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--box-info-color, #fff);
  color: var(--textDefault);
  border-radius: 12px;
  box-shadow: 0 2px 16px #000a;
  padding: 12px;
  z-index: 100;
  min-width: 280px;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Arrow = styled.button`
  background: none;
  border: none;
  color: #b9b9c3;
  font-size: 1.2em;
  cursor: pointer;
`;

const DaysRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const DayCell = styled.div<{
  selected?: boolean;
  faded?: boolean;
  isStart?: boolean;
  isEnd?: boolean;
  isPreview?: boolean;
}>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  margin: 1px;
  background: ${({ selected, isStart, isEnd, isPreview }) =>
    isStart || isEnd
      ? 'var(--quick-selector-background, #CECEF0)'
      : selected
      ? 'var(--active-channel-selector, #e2e3f5)'
      : isPreview
      ? 'var(--active-channel-selector, #e2e3f5)'
      : 'none'};
  color: ${({ faded, selected, isStart, isEnd, isPreview }) =>
    isStart || isEnd
      ? 'var(--primary)'
      : faded
      ? '#666'
      : 'var(--textDefault)'};
  border: ${({ isStart, isEnd, selected, isPreview }) =>
    isStart || isEnd
      ? '2px solid #fff'
      : selected || isPreview
      ? '1px solid var(--secondary-background-color)'
      : 'none'};
  cursor: pointer;
  &:hover {
    background: 'var(--tertiary-background-color)';
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  margin-top: 8px;
  cursor: pointer;
  float: right;
  font-size: 1em;
  opacity: 0.7;
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.9em;
  text-align: center;
  margin-top: 4px;
`;

const Weekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export const DatePickerPopover = ({
  value,
  onChange,
  onClear,
  onClose,
}: {
  value: { startDate: Date | null; endDate: Date | null };
  onChange: (range: { startDate: Date; endDate: Date }) => void;
  onClear: () => void;
  onClose: () => void;
}) => {
  const [currentMonth, setCurrentMonth] = useState(
    value.startDate || new Date()
  );
  const [range, setRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(value);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  function handleDayClick(day: Date) {
    if (!range.startDate || (range.startDate && range.endDate)) {
      setRange({ startDate: day, endDate: null });
    } else if (range.startDate && !range.endDate) {
      if (day < range.startDate) {
        setRange({ startDate: day, endDate: range.startDate });
      } else {
        setRange({ startDate: range.startDate, endDate: day });
      }
    }
  }

  function handleDayHover(day: Date) {
    setHoveredDate(day);
  }

  function handleDayLeave() {
    setHoveredDate(null);
  }

  function renderDays() {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = '';

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, 'd');
        const isFaded = !isSameMonth(day, monthStart);
        const isFuture = isAfter(day, endOfDay(new Date()));
        const dayCopy = new Date(day); // Cópia imutável

        const isInRange = !!(
          range.startDate &&
          range.endDate &&
          day >= range.startDate &&
          day <= range.endDate
        );
        const isStart = range.startDate && isSameDay(day, range.startDate);
        const isEnd = range.endDate && isSameDay(day, range.endDate);

        // Lógica para preview do range
        let isPreview = false;
        if (range.startDate && !range.endDate && hoveredDate && !isFuture) {
          const previewStart =
            range.startDate < hoveredDate ? range.startDate : hoveredDate;
          const previewEnd =
            range.startDate < hoveredDate ? hoveredDate : range.startDate;
          isPreview = day >= previewStart && day <= previewEnd && !isStart;
        }

        days.push(
          <DayCell
            key={dayCopy.toString()}
            faded={!!isFaded || isFuture}
            selected={isInRange}
            isStart={!!isStart}
            isEnd={!!isEnd}
            isPreview={isPreview}
            onClick={() => {
              if (!isFuture) handleDayClick(dayCopy);
            }}
            onMouseEnter={() => {
              if (!isFuture) handleDayHover(dayCopy);
            }}
            onMouseLeave={handleDayLeave}
            style={isFuture ? { pointerEvents: 'none', opacity: 0.4 } : {}}
          >
            {formattedDate}
          </DayCell>
        );
        day = addDays(day, 1);
      }
      rows.push(<DaysRow key={day.toString()}>{days}</DaysRow>);
      days = [];
    }
    return rows;
  }

  return (
    <Popover ref={ref}>
      <CalendarHeader>
        <Arrow onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          {'←'}
        </Arrow>
        <span style={{ fontWeight: 500 }}>
          {capitalizeFirstLetter(
            format(currentMonth, 'MMMM yyyy', { locale: ptBR })
          )}
        </span>
        <Arrow onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          {'→'}
        </Arrow>
      </CalendarHeader>
      <DaysRow>
        {Weekdays.map((d, index) => (
          <DayCell
            as="span"
            key={`${d}_${index}`}
            style={{ fontWeight: 600, color: 'var(--textDefault)' }}
          >
            {d}
          </DayCell>
        ))}
      </DaysRow>
      {renderDays()}
      <ButtonContainer>
        <ClearButton
          onClick={() => {
            setRange({ startDate: null, endDate: null });
            setErrorMessage('');
            onClear();
          }}
        >
          Limpar
        </ClearButton>
        <Button
          onClick={() => {
            if (range.startDate && !range.endDate) {
              setErrorMessage('');
              onChange({
                startDate: range.startDate,
                endDate: range.startDate,
              });
              return;
            }

            if (range.startDate && range.endDate) {
              // 🔒 Regra de negócio: validação de range máximo de 6 meses
              const diffLimit = addMonths(range.startDate, 6);
              if (isAfter(range.endDate, diffLimit)) {
                setErrorMessage('O intervalo não pode ser superior a 6 meses.');
                return;
              }

              setErrorMessage('');
              onChange({
                startDate: range.startDate,
                endDate: range.endDate,
              });
              return;
            }

            setErrorMessage('Selecione uma data inicial para continuar');
          }}
        >
          Confirmar
        </Button>
      </ButtonContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Popover>
  );
};
